import { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, CreditCard, AlertCircle, RefreshCw, Info } from 'lucide-react';
import api from '../../lib/api';
import { PAYPAL_PENDING_KEY, readPendingPayPal, clearPendingPayPal } from '../../lib/inAppBrowser';

const START_ERROR = "We couldn't start PayPal checkout. Please check your connection and try again.";

export const PayPalRedirectCheckout = ({ amount, currency }) => {
  const [starting, setStarting] = useState(null);
  const [error, setError] = useState(null);
  const [lastFunding, setLastFunding] = useState('paypal');
  const [unfinished, setUnfinished] = useState(() => readPendingPayPal());
  const value = parseFloat(amount);
  const valid = value >= 1 && value <= 10000;

  const start = async (funding) => {
    setError(null);
    setLastFunding(funding);
    setStarting(funding);
    try {
      const { data } = await api.post('/paypal/create-order', { amount: value, currency, flow: 'redirect', funding });
      if (!data.approve_url) throw new Error('missing approve url');
      localStorage.setItem(PAYPAL_PENDING_KEY, JSON.stringify({ orderId: data.order_id, amount: value, currency, funding, ts: Date.now() }));
      window.location.assign(data.approve_url);
    } catch (e) {
      const detail = e.response?.status === 400 ? e.response.data?.detail : null;
      setError(detail || START_ERROR);
      setStarting(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="paypal-redirect-checkout">
      {unfinished && !error && (
        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border border-border" data-testid="paypal-unfinished-note">
          <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">Your last PayPal checkout wasn't completed. You have not been charged.</p>
            <button type="button" className="text-xs underline text-muted-foreground mt-1" onClick={() => { clearPendingPayPal(); setUnfinished(null); }} data-testid="paypal-unfinished-dismiss">Dismiss</button>
          </div>
        </div>
      )}
      {error && (
        <div className="space-y-3" data-testid="paypal-start-error">
          <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          <Button variant="outline" className="w-full" onClick={() => start(lastFunding)} disabled={!valid} data-testid="paypal-start-retry-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Try again
          </Button>
        </div>
      )}
      <Button
        onClick={() => start('paypal')}
        disabled={!valid || !!starting}
        className="w-full h-12 bg-[#ffc439] hover:bg-[#f0b82d] text-[#003087] font-semibold rounded-md"
        data-testid="paypal-redirect-paypal-btn"
      >
        {starting === 'paypal' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        {unfinished ? 'Try again with PayPal' : 'Pay with PayPal'}
      </Button>
      <Button
        onClick={() => start('card')}
        disabled={!valid || !!starting}
        className="w-full h-12 bg-[#2c2e2f] hover:bg-black text-white font-semibold rounded-md"
        data-testid="paypal-redirect-card-btn"
      >
        {starting === 'card' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
        Debit or Credit Card
      </Button>
      <p className="text-xs text-muted-foreground text-center">You'll be taken to PayPal's secure page and brought back here when you're done.</p>
    </div>
  );
};
